import z from "zod";

export enum CriteriaType {
  VARIANT_REACHED_MIN_ROI = "VARIANT_REACHED_MIN_ROI",
  FIRST_TO_MIN_ROI = "FIRST_TO_MIN_ROI",
  HIGHEST_ROI_AT_TIME = "HIGHEST_ROI_AT_TIME",
}

const BasePartialSendInputSchema = z.object({
  variantIds: z.array(z.string().uuid()).min(1),

  // Number of minutes to wait after sending to begin evaluating.
  evaluationDelayInMin: z.coerce.number().int().optional().default(0),

  // Exact time at which the evaluation window expires.
  expiresAt: z.coerce.date(),

  // Intended for evaluations that have an evaluationDelayInMin and are required to run only once.
  runOnce: z.boolean().optional().default(false),

  // Send to full contact list if evaluation is successful.
  sendToFullListOnSuccess: z.boolean().default(false),
});

const BaseRoiDonationInputSchema = BasePartialSendInputSchema.extend({
  minRoi: z.coerce
    .number()
    .nullish()
    .transform(v => v ?? 0),
  minNumberOfDonations: z.coerce
    .number()
    .int()
    .nullish()
    .transform(v => v ?? 0),
  excludeDonationsOverInCents: z.coerce.number().int().optional(),
});

const FirstToMinRoiCriteriaSchema = BaseRoiDonationInputSchema.extend({
  type: z.literal(CriteriaType.FIRST_TO_MIN_ROI),
  minRoi: z.coerce.number().gt(0).default(0),
});

const HighestRoiAtTimeCriteriaBaseSchema = BaseRoiDonationInputSchema.extend({
  type: z.literal(CriteriaType.HIGHEST_ROI_AT_TIME),
});

const HighestRoiAtTimeCriteriaSchema =
  HighestRoiAtTimeCriteriaBaseSchema.superRefine((data, ctx) => {
    if (!data.evaluationDelayInMin) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Evaluation delay must be specified",
        path: ["evaluationDelayInMin"],
      });
    }
  }).transform(data => ({
    ...data,
    runOnce: true,
  }));

const VariantReachedMinRoiCriteriaBaseSchema =
  BaseRoiDonationInputSchema.extend({
    type: z.literal(CriteriaType.VARIANT_REACHED_MIN_ROI),
  });

const VariantReachedMinRoiCriteriaSchema =
  VariantReachedMinRoiCriteriaBaseSchema.superRefine((data, ctx) => {
    if (data.minNumberOfDonations === 0 && data.minRoi === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          "Either a minimum ROI or minimum number of donations must be specified",
        path: ["minRoi"],
      });
    }
  });

export const EvaluationCriteriaFormSchema = z
  .discriminatedUnion("type", [
    VariantReachedMinRoiCriteriaBaseSchema,
    FirstToMinRoiCriteriaSchema,
    HighestRoiAtTimeCriteriaBaseSchema,
  ])
  .superRefine((data, ctx) => {
    if (data.type === CriteriaType.VARIANT_REACHED_MIN_ROI) {
      if (data.minNumberOfDonations === 0 && data.minRoi === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            "Either a minimum ROI or minimum number of donations must be specified",
          path: ["minRoi"],
        });
      }
    }
  });

export type FirstToMinRoiCriteriaInput = z.input<
  typeof FirstToMinRoiCriteriaSchema
>;
export type VariantReachedMinRoiCriteriaInput = z.input<
  typeof VariantReachedMinRoiCriteriaSchema
>;
export type HighestRoiAtTimeCriteriaInput = z.input<
  typeof HighestRoiAtTimeCriteriaSchema
>;

export const EvaluationCriteriaSchema = z.union([
  VariantReachedMinRoiCriteriaSchema,
  FirstToMinRoiCriteriaSchema,
  HighestRoiAtTimeCriteriaSchema,
]);

export type EvaluationCriteria = z.infer<typeof EvaluationCriteriaSchema>;

const VariantMinRoiResultSchema = z.object({
  partialSendEvaluationId: z.string().uuid(),
  variantId: z.string().uuid(),
  roi: z.number(),
  donationCount: z.number(),
  reachedMinRoi: z.boolean(),
  excludedDonations: z.array(z.number().int()).default([]),
});

type VariantMinRoiResult = z.infer<typeof VariantMinRoiResultSchema>;

const MinRoiResultSchema = z.object({
  partialSendEvaluationId: z.string().uuid(),
  winningVariantId: z.string().uuid().optional(),
  variantResults: z.array(VariantMinRoiResultSchema),
});

export const EvaluationResultSchema = z.discriminatedUnion("type", [
  z
    .object({ type: z.literal(CriteriaType.VARIANT_REACHED_MIN_ROI) })
    .merge(VariantMinRoiResultSchema),
  z
    .object({ type: z.literal(CriteriaType.FIRST_TO_MIN_ROI) })
    .merge(MinRoiResultSchema),
  z
    .object({ type: z.literal(CriteriaType.HIGHEST_ROI_AT_TIME) })
    .merge(MinRoiResultSchema),
]);

export type EvaluationResult = z.infer<typeof EvaluationResultSchema>;

function buildExcludedDonationsMap(
  variantResults: VariantMinRoiResult[]
): Record<string, number> {
  const map: Record<string, number> = {};
  for (const vr of variantResults) {
    map[vr.variantId] = vr.excludedDonations.length;
  }
  return map;
}

const EvaluationSuccessEventSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal(CriteriaType.VARIANT_REACHED_MIN_ROI),
    partialSendEvaluationId: z.string().uuid(),
    variantId: z.string().uuid(),
    roi: z.number(),
    excludedDonationsByScriptVariantId: z.record(z.string(), z.number()),
  }),
  z.object({
    type: z.literal(CriteriaType.FIRST_TO_MIN_ROI),
    partialSendEvaluationId: z.string().uuid(),
    bestScriptVariantId: z.string().uuid().optional(),
    bestScriptVariantRoi: z.number(),
    excludedDonationsByScriptVariantId: z.record(z.string(), z.number()),
  }),
  z.object({
    type: z.literal(CriteriaType.HIGHEST_ROI_AT_TIME),
    partialSendEvaluationId: z.string().uuid(),
    bestScriptVariantId: z.string().uuid().optional(),
    bestScriptVariantRoi: z.number(),
    excludedDonationsByScriptVariantId: z.record(z.string(), z.number()),
  }),
]);

export type EvaluationSuccessEvent = z.infer<
  typeof EvaluationSuccessEventSchema
>;

export function buildSuccessEvent(
  result: EvaluationResult
): EvaluationSuccessEvent {
  if (result.type === CriteriaType.VARIANT_REACHED_MIN_ROI) {
    return EvaluationSuccessEventSchema.parse({
      type: result.type,
      partialSendEvaluationId: result.partialSendEvaluationId,
      variantId: result.variantId,
      roi: result.roi,
      excludedDonationsByScriptVariantId: {
        [result.variantId]: result.excludedDonations.length,
      },
    });
  }

  return EvaluationSuccessEventSchema.parse({
    type: result.type,
    partialSendEvaluationId: result.partialSendEvaluationId,
    bestScriptVariantId: result.winningVariantId,
    bestScriptVariantRoi: result.variantResults.reduce(
      (max, vr) => Math.max(max, vr.roi),
      0
    ),
    excludedDonationsByScriptVariantId: buildExcludedDonationsMap(
      result.variantResults
    ),
  });
}
